/**
 * LoginPage — Screen 1 of the auth flow (design: 05-auth.md, 39b-google-signin-ui.md).
 *
 * Route: /login?return=<path>&error=<code>
 *
 * Accessibility:
 *   TC-012: All inputs have associated <label> elements.
 *   TC-013: On submit failure, focus moves to the email field (first error).
 *   TC-014: Password visibility toggle is keyboard-accessible.
 *   AC-8 (39b): Google button has visible text label; logo SVG is aria-hidden.
 *
 * Validation:
 *   - Email: validated on blur; re-validated on submit.
 *   - On 401: generic error "Email or password is incorrect" shown adjacent
 *     to the field group (not in a top banner).
 *   - On 429: rate-limit message; form fields disabled.
 *
 * OAuth (39b):
 *   - AC-1: "Continue with Google" button above email/password form.
 *   - AC-4: ?error=oauth_failed shows inline error below Google button.
 *   - AC-10: return URL passed through to Google initiation endpoint.
 */

import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { AuthCard } from './AuthCard';
import { EmailField } from './EmailField';
import { PasswordField } from './PasswordField';
import { GoogleSignInButton } from './GoogleSignInButton';
import { login as loginApi, AuthApiError } from '../../api/auth';

function isValidEmail(value: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());
}

export function LoginPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const returnTo = searchParams.get('return') ?? '/';

  // AC-4: show OAuth error message when ?error=oauth_failed is in the URL.
  // The message is cleared on the first email field interaction (state-driven dismissal).
  const [oauthError, setOauthError] = useState(
    searchParams.get('error') === 'oauth_failed'
  );

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [emailError, setEmailError] = useState('');
  const [formError, setFormError] = useState('');
  const [rateLimited, setRateLimited] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  function validateEmail(): boolean {
    if (!email.trim()) {
      setEmailError('Please enter your email address.');
      return false;
    }
    if (!isValidEmail(email)) {
      setEmailError('Please enter a valid email address.');
      return false;
    }
    setEmailError('');
    return true;
  }

  function handleEmailChange(value: string) {
    setEmail(value);
    // Dismiss the OAuth error when the user starts interacting with the form.
    if (oauthError) setOauthError(false);
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setFormError('');

    const emailOk = validateEmail();

    if (!emailOk) {
      // TC-013: focus on the first field with an error.
      const emailInput = document.getElementById('login-email');
      if (emailInput instanceof HTMLInputElement) emailInput.focus();
      return;
    }

    setSubmitting(true);
    try {
      await loginApi(email, password);
      // Redirect to return URL (TC-010: server validates; client trusts server).
      navigate(returnTo, { replace: true });
    } catch (err) {
      if (err instanceof AuthApiError) {
        if (err.status === 429) {
          setRateLimited(true);
          setFormError('Too many login attempts. Please wait 15 minutes and try again.');
        } else {
          // TC-005: generic message — does not reveal whether email exists.
          setFormError('Email or password is incorrect.');
          // TC-013: move focus to email field on error.
          const emailInput = document.getElementById('login-email');
          if (emailInput instanceof HTMLInputElement) emailInput.focus();
        }
      } else {
        setFormError('Something went wrong. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  }

  const disabled = submitting || rateLimited;

  return (
    <AuthCard>
      <h1 className="text-lg font-bold text-gray-900 mb-6">Log in</h1>

      {/* AC-1 (39b): Google button above the email/password form. */}
      <GoogleSignInButton returnTo={returnTo} />

      {/* AC-4 (39b): OAuth error message — shown when ?error=oauth_failed, below Google button. */}
      {oauthError && (
        <p
          role="alert"
          aria-live="polite"
          className="mt-3 mb-1 text-sm text-red-600 flex items-start gap-1"
        >
          <span aria-hidden="true">&#9888;</span>
          Google sign-in failed. Please try again, or use your email and password.
        </p>
      )}

      {/* "or" divider (design: 39b-google-signin-ui.md). */}
      <div className="flex items-center gap-3 my-4">
        <div className="flex-1 border-t border-hairline" />
        <span className="text-xs text-gray-400">or</span>
        <div className="flex-1 border-t border-hairline" />
      </div>

      <form onSubmit={handleSubmit} noValidate>
        <EmailField
          id="login-email"
          label="Email"
          value={email}
          onChange={handleEmailChange}
          onBlur={validateEmail}
          error={emailError}
          autoComplete="email"
        />

        <PasswordField
          id="login-password"
          label="Password"
          value={password}
          onChange={setPassword}
          autoComplete="current-password"
        />

        {/* Form-level error (TC-013: adjacent to field group, not top banner) */}
        {formError && (
          <p
            id="login-form-error"
            role="alert"
            aria-live="polite"
            className="mb-4 text-sm text-red-600 flex items-start gap-1"
          >
            <span aria-hidden="true">&#9888;</span>
            {formError}
          </p>
        )}

        <div className="mb-4 text-right">
          <Link
            to={`/forgot-password`}
            className="text-sm text-brand-primary hover:underline"
          >
            Forgot password?
          </Link>
        </div>

        <button
          type="submit"
          disabled={disabled}
          className="w-full bg-brand-primary text-white font-semibold py-2.5 min-h-[44px] rounded-lg hover:bg-brand-primary-dk focus:outline-none focus:ring-2 focus:ring-brand-primary focus:ring-offset-2 motion-safe:transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {submitting ? 'Logging in…' : 'Log in'}
        </button>
      </form>

      <p className="mt-4 text-sm text-center text-gray-500">
        Don&apos;t have an account?{' '}
        <Link
          to={`/signup${returnTo !== '/' ? `?return=${encodeURIComponent(returnTo)}` : ''}`}
          className="text-brand-primary hover:underline font-medium"
        >
          Create one
        </Link>
      </p>
    </AuthCard>
  );
}
